import type { GridApi, ValueGetterParams } from "ag-grid-enterprise";
import { PER_SQM_SUFFIX, STORE_SQM_METRIC } from "./metricConstants";
import { OPTION_DERIVED_EXPRESSIONS } from "./optionDerivedExpressions";
import { createRowId } from "./rowId";

/**
 * Checks if a metric is a per-SQM derived metric.
 */
export function isPerSqmMetric(metricName: string): boolean {
  return metricName.includes(PER_SQM_SUFFIX);
}

/**
 * Extracts the source metric name from a per-SQM metric.
 * e.g. "GS2_PER_SQM_LOCATION_AVG" → "GS2"
 *      "END_OF_PERIOD_UNITS_PER_SQM_LOCATION_AVG" → "END_OF_PERIOD_UNITS"
 */
export function getSourceMetricName(perSqmMetricName: string): string {
  return perSqmMetricName.split(PER_SQM_SUFFIX)[0];
}

/**
 * Computes the per-SQM value for a cell: sourceMetric / STORE_SQM.
 * Returns null if STORE_SQM is zero/missing or source metric is missing.
 */
export function calculatePerSqm(
  data: Record<string, unknown>,
  colId: string,
  api: GridApi
): number | null {
  const fiscalYearName = data.fiscal_year_name as string;
  const metricName = data.metric_name as string;
  const producerType = data.producer_type as string;

  // Look up the STORE_SQM row
  const sqmRowId = createRowId(fiscalYearName, STORE_SQM_METRIC, producerType);
  const sqmNode = api.getRowNode(sqmRowId);
  const sqmValue = sqmNode?.data?.[colId] as number | null | undefined;
  if (!sqmValue || sqmValue === 0) return null;

  // Look up the source metric row (e.g. GS2 for GS2_PER_SQM_LOCATION_AVG)
  const sourceMetric = getSourceMetricName(metricName);
  const sourceRowId = createRowId(fiscalYearName, sourceMetric, producerType);
  const sourceNode = api.getRowNode(sourceRowId);
  const sourceValue = sourceNode?.data?.[colId] as number | null | undefined;
  if (sourceValue == null) return null;

  return sourceValue / sqmValue;
}

/**
 * Checks if a metric is an OPTION derived metric (computed client-side).
 */
export function isOptionDerivedMetric(metricName: string): boolean {
  return metricName in OPTION_DERIVED_EXPRESSIONS;
}

/**
 * Resolves a variable's value for the given column. If the variable is itself
 * a derived OPTION metric, computes it recursively (one level deep).
 */
function resolveVariable(
  variableName: string,
  fiscalYearName: string,
  producerType: string,
  colId: string,
  api: GridApi
): number | null {
  const rowId = createRowId(fiscalYearName, variableName, producerType);
  const node = api.getRowNode(rowId);
  const rawValue = node?.data?.[colId] as number | null | undefined;

  // If the variable is itself a derived OPTION metric and has no raw value,
  // compute it recursively.
  if (rawValue == null && variableName in OPTION_DERIVED_EXPRESSIONS) {
    return calculateOptionDerived(
      variableName,
      fiscalYearName,
      producerType,
      colId,
      api
    );
  }

  return rawValue ?? null;
}

/**
 * Computes an OPTION derived metric value for a specific cell.
 */
export function calculateOptionDerived(
  metricName: string,
  fiscalYearName: string,
  producerType: string,
  colId: string,
  api: GridApi
): number | null {
  const expr = OPTION_DERIVED_EXPRESSIONS[metricName];
  if (!expr) return null;

  const values = expr.variables.map((v) =>
    resolveVariable(v, fiscalYearName, producerType, colId, api)
  );

  return expr.evaluate(values);
}

/**
 * Factory returning a ValueGetterFunc for ABS mode that handles derived metrics.
 * Per-SQM metrics are computed on-the-fly from source metric / STORE_SQM.
 * OPTION derived metrics are computed from their expression definitions.
 * All other metrics return the raw data value.
 */
export function createDerivedValueGetter(): (
  params: ValueGetterParams
) => unknown {
  return (params: ValueGetterParams) => {
    const colId = params.column.getColId();
    const { metric_name } = params.data;

    if (isPerSqmMetric(metric_name)) {
      return calculatePerSqm(params.data, colId, params.api);
    }

    if (isOptionDerivedMetric(metric_name)) {
      const { fiscal_year_name, producer_type } = params.data;
      return calculateOptionDerived(
        metric_name,
        fiscal_year_name,
        producer_type,
        colId,
        params.api
      );
    }

    return params.data[colId];
  };
}

/**
 * Time period column keys that may need derived value enrichment.
 */
const TIME_PERIOD_PATTERN = /^[WMQ]\d+$/;

/**
 * Enriches selected rows with computed derived values.
 * AG Grid's getSelectedRows() returns raw data, so per-SQM and OPTION derived
 * metrics will be missing their computed values. This function fills them in
 * using the grid API, mirroring the old Dash calculateDerivedMetricsOnSelection.
 */
export function enrichDerivedRows(
  rows: Record<string, unknown>[],
  api: GridApi
): Record<string, unknown>[] {
  return rows.map((row) => {
    const metricName = row.metric_name as string;
    const isDerived =
      isPerSqmMetric(metricName) || isOptionDerivedMetric(metricName);
    if (!isDerived) return row;

    const fiscalYearName = row.fiscal_year_name as string;
    const producerType = row.producer_type as string;
    const enriched = { ...row };

    for (const key of Object.keys(row)) {
      if (TIME_PERIOD_PATTERN.test(key) || key === "PTG" || key === "TTL") {
        if (isPerSqmMetric(metricName)) {
          enriched[key] = calculatePerSqm(row, key, api);
        } else {
          enriched[key] = calculateOptionDerived(
            metricName,
            fiscalYearName,
            producerType,
            key,
            api
          );
        }
      }
    }
    return enriched;
  });
}
